<template>
  <b-row class="mb-3">
    <b-col md="3">
      <b-form-group :label="$t('global.object.period')">
        <date-picker
          ref="periodMonthPicker"
          v-model="period"
          type="month"
          format="MMM YYYY"
          value-type="YYYY-MM-DD"
          :placeholder="$t('global.object.period')"
          :confirm="true"
          :confirm-text="$t('global.action.apply')"
          :disabled-date="disabledRange"
          :lang="datePickerLang"
        >
          <template #footer>
            <b-button class="mx-2" variant="outline-primary" @click="closeCalendar()">
              {{ $t('global.action.cancel') }}
            </b-button>
          </template>
        </date-picker>
      </b-form-group>
    </b-col>
    <b-col v-if="isParent() && hasAccess(['a12a-fitur-child-monitoring:read'])" md="3">
      <b-form-group
        id="branch"
        label="Domain"
        :state="branches !== null"
        :label-class="{ required: !multiple }"
      >
        <multiselect
          id="branch"
          v-model="branches"
          :options="branchOptions"
          label="name"
          track-by="id"
          :disabled="isLoadingBranches || isBranchDisabled"
          :placeholder="$t('global.form.select') + ' Domain'"
          :required="multiple === false ? true : false"
          :multiple="multiple"
          :class="{ 'is-invalid': branches === null && !multiple }"
        >
          <template v-if="!multiple" slot="selection" slot-scope="{ values }">
            <span v-if="values.length" class="multiselect__single">
              {{ values[0].name }}
            </span>
          </template>
          <template v-else slot="selection" slot-scope="{ values, isOpen }">
            <div v-if="values.length && !isOpen" class="multiselect__tags-wrap">
              <span class="multiselect__tag">
                <span>{{ values[0].name }}</span>
                <i
                  aria-hidden="true"
                  tabindex="1"
                  class="multiselect__tag-icon"
                  @click="deleteItem(values[0].id, branches, 'id')"
                ></i>
              </span>
              <span
                v-if="values.length > 1 && !isOpen"
                class="badge bg-success px-2 py-1 d-inline-block"
                >+{{ values.length - 1 }}
              </span>
            </div>
          </template>
        </multiselect>
        <b-form-invalid-feedback v-if="!multiple" :state="branches !== null">
          {{ 'Domain ' + $t('global.validation.required') }}
        </b-form-invalid-feedback>
      </b-form-group>
    </b-col>
    <b-col md="3">
      <b-form-group id="fleet-group" :label="$t('global.object.fleetGroup')">
        <multiselect
          id="fleet-group"
          v-model="fleetGroups"
          :options="fleetGroupOptions"
          label="name"
          track-by="id"
          :multiple="true"
          :disabled="isSelectionDisabled"
          :placeholder="$t('global.form.select') + ' ' + $t('global.object.fleetGroup')"
        >
          <template slot="selection" slot-scope="{ values, isOpen }">
            <div v-if="values.length && !isOpen" class="multiselect__tags-wrap">
              <span class="multiselect__tag">
                <span>{{ values[0].name }}</span>
                <i
                  aria-hidden="true"
                  tabindex="1"
                  class="multiselect__tag-icon"
                  @click="deleteItem(values[0].id, fleetGroups, 'id')"
                ></i>
              </span>
              <span
                v-if="values.length > 1 && !isOpen"
                class="badge bg-success px-2 py-1 d-inline-block"
                >+{{ values.length - 1 }}
              </span>
            </div>
          </template>
        </multiselect>
      </b-form-group>
    </b-col>
    <b-col md="3">
      <b-form-group
        id="fleet-category"
        :label="$t('global.field.vehicleType')"
        label-for="category-option"
      >
        <multiselect
          id="category-option"
          v-model="fleetTypes"
          :options="fleetTypeOptions"
          track-by="vehicleTypeId"
          label="label"
          :disabled="isSelectionDisabled"
          :placeholder="$t('global.field.vehicleType')"
          :multiple="true"
        >
          <template slot="selection" slot-scope="{ values, isOpen }">
            <div v-if="values.length && !isOpen" class="multiselect__tags-wrap">
              <span class="multiselect__tag">
                <span>{{ values[0].label }}</span>
                <i
                  aria-hidden="true"
                  tabindex="1"
                  class="multiselect__tag-icon"
                  @click="deleteItem(values[0].vehicleTypeId, fleetTypes, 'vehicleTypeId')"
                ></i>
              </span>
              <span
                v-if="values.length > 1 && !isOpen"
                class="badge bg-success px-2 py-1 d-inline-block"
                >+{{ values.length - 1 }}
              </span>
            </div>
          </template>
        </multiselect>
      </b-form-group>
    </b-col>
  </b-row>
</template>

<script>
import appConfig from '@/app.config'
import { Helper } from '@/mixin/helper'
import Multiselect from 'vue-multiselect'
import { mapGetters } from 'vuex'

import { MixPanel } from '@/mixin/mixpanel'
import { GET_VEHICLE_TYPE } from '@/services/api/setting/fleet/fleetType'
import { GET_SUB_DOMAINS } from '@/services/api/setting/personalization/domainManagement'
import datePickerLang from '@/helpers/date-picker-lang'
import moment from 'moment'

export default {
  page: {
    title: 'Dashboard Management Filter',
    meta: [
      {
        name: 'description',
        content: appConfig.description,
      },
    ],
  },
  components: {
    Multiselect,
  },
  mixins: [Helper, MixPanel],
  apollo: {
    vehicleTypes: {
      fetchPolicy: 'no-cache',
      query: GET_VEHICLE_TYPE,
      variables() {
        return {
          filter: {
            hasCompanyVehicle: true,
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          this.fleetTypeOptions = data.vehicleTypes.vehicleTypes.data.map((item) => {
            return {
              vehicleTypeId: item.vehicleTypeId,
              label: `${item.model}/${item.type}`,
            }
          })
        }
      },
    },
  },

  data() {
    return {
      fleetGroups: [],
      period: '',
      fleetTypes: [],
      fleetTypeOptions: [],
      multiple: false,
      branches: this.multiple ? [] : null,
      branchOptions: [],
      isLoadingBranches: true,
      temporaryBranch: null,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      isParent: 'identity/isParent',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    fleetGroupOptions() {
      const options = this.$store.state.access.vGroups
      return options
    },
    datePickerLang() {
      return datePickerLang()
    },
    isBranchDisabled() {
      return this.fleetGroups.length > 0 || this.fleetTypes.length > 0
    },
    isSelectionDisabled() {
      if (!this.isParent() || !this.hasAccess(['a12a-fitur-child-monitoring:read'])) {
        return false
      }
 
      if (!this.multiple) {
        return !this.branches
      }
 
      return !this.branches || this.branches.length === 0
    },
  },
  watch: {
    fleetGroups: {
      handler() {
        this.$emit('updateFleetGroups', this.fleetGroups)
      },
    },
    fleetTypes: {
      handler() {
        this.$emit('updateFleetTypes', this.fleetTypes)
      },
    },
    period: {
      handler() {
        this.$emit('updatePeriod', this.period)
      },
    },
    branches: {
      handler(newVal) {
        if (!newVal && !this.multiple) {
          return
        }

        if (JSON.stringify(newVal) === JSON.stringify(this.temporaryBranch)) {
          return false
        }

        const resetFleet = () => {
          this.fleetGroups = []
          this.fleetTypes = []
        }

        if (!newVal || (this.multiple && newVal.length === 0)) {
          this.$emit('updateBranches', this.multiple ? this.branchOptions : [])
          this.temporaryBranch = newVal
          return
        }

        resetFleet()
        this.$emit('updateBranches', this.multiple ? this.branches : [newVal])
        this.temporaryBranch = newVal
      },
    },
  },
  created() {
    this.multiple = this.getFeatureFlagValue('multiDomainSummaryDashboardFlag') || false
  },
  mounted() {
    this.period = moment().subtract(1, 'month').format('YYYY-MM-DD')
    this.$emit('updateFleetGroups', this.fleetGroups)
    this.$emit('updateFleetTypes', this.fleetTypes)
    this.fetchSubDomains()
  },
  methods: {
    closeCalendar() {
      this.$refs.periodMonthPicker.defaultOpen = false
    },
    deleteItem(id, array, key) {
      const index = array.findIndex((item) => item[key] === id)
      if (index !== -1) {
        array.splice(index, 1)
      }
    },
    disabledRange: function (date) {
      return date >= moment().subtract(1, 'months').endOf('month')
    },
    async fetchSubDomains() {
      const { data } = await this.$apollo.query({
        query: GET_SUB_DOMAINS,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            orderBy: [JSON.stringify({ name: 'asc' })],
            includeParent: true,
          },
        },
      })

      if (data && data.domainManagements) {
        this.branchOptions =
          data.domainManagements?.domainManagements?.data?.map((item) => ({
            id: item.companyId,
            name: item.companyName,
          })) || []

        const companyIndex = this.branchOptions.findIndex((item) => item.id === this.getCompanyId())
        if (companyIndex > -1) {
          const company = this.branchOptions.splice(companyIndex, 1)[0]
          this.branchOptions.unshift(company)
          if (this.isParent() && this.hasAccess(['a12a-fitur-child-monitoring:read'])) {
            this.branches = this.multiple ? [company] : company
          }
        }
      }
      this.isLoadingBranches = false
    },
  },
}
</script>
